import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  doc,
  getDocs,
  getFirestore,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { Button, Table, Form } from 'react-bootstrap';
import { FaClipboardCheck, FaPlus, FaSave, FaTrashAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';
import DocumentosHistoricosAlumno from './DocumentosHistoricosAlumno';
import './SesionesAlumno.scss';

const DRAFT_PREFIX = 'draft-session-';
const SESSION_FIELDS = ['fecha', 'modulo', 'ejercicios', 'velocidad', 'comprension', 'lecturaRealizada', 'observaciones'];

const buildBlankSession = (alumnoId) => ({
  alumnoId,
  fecha: '',
  modulo: '',
  ejercicios: '',
  velocidad: '',
  comprension: '',
  lecturaRealizada: '',
  observaciones: '',
});

const buildSessionPayload = (session) => ({
  alumnoId: session.alumnoId,
  fecha: session.fecha || '',
  modulo: session.modulo || '',
  ejercicios: session.ejercicios || '',
  velocidad: session.velocidad || '',
  comprension: session.comprension || '',
  lecturaRealizada: session.lecturaRealizada || '',
  observaciones: session.observaciones || '',
});

const serializeSession = (session) => JSON.stringify(buildSessionPayload(session));
const isDraftSession = (session) => Boolean(session?.isDraft || String(session?.id || '').startsWith(DRAFT_PREFIX));
const isSessionBlank = (session) => SESSION_FIELDS.every((field) => !String(session?.[field] || '').trim());
const createSessionDocumentId = () => (
  window.crypto?.randomUUID?.()
  || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

export const compareModulesDescending = (first, second) => {
  const firstNumber = Number(first?.modulo);
  const secondNumber = Number(second?.modulo);
  const firstIsNumber = Number.isFinite(firstNumber);
  const secondIsNumber = Number.isFinite(secondNumber);

  if (firstIsNumber && secondIsNumber) return secondNumber - firstNumber;
  if (firstIsNumber) return -1;
  if (secondIsNumber) return 1;
  return String(second?.modulo || '').localeCompare(String(first?.modulo || ''), 'es', { numeric: true });
};

const getRegistrationTime = (session) => {
  const value = session?.fecha || session?.createdAt;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();

  const time = value instanceof Date ? value.getTime() : new Date(value || 0).getTime();
  return Number.isFinite(time) ? time : 0;
};

export const getSessionDateInputValue = (session) => {
  const value = session?.fecha || session?.createdAt;
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const date = typeof value?.toDate === 'function'
    ? value.toDate()
    : value instanceof Date
      ? value
      : new Date(value);

  if (!Number.isFinite(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatSessionDate = (session) => {
  const value = session?.createdAt;
  const date = typeof value?.toDate === 'function'
    ? value.toDate()
    : value instanceof Date
      ? value
      : new Date(value || '');

  if (!Number.isFinite(date.getTime())) {
    return isDraftSession(session) ? 'Al guardar' : 'Sin fecha';
  }

  return new Intl.DateTimeFormat('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const compareSessionsForDisplay = (first, second) => {
  const moduleComparison = compareModulesDescending(first, second);
  if (moduleComparison !== 0) return moduleComparison;

  const dateComparison = getRegistrationTime(second) - getRegistrationTime(first);
  if (dateComparison !== 0) return dateComparison;

  return (first?._loadOrder || 0) - (second?._loadOrder || 0);
};

export const normalizeSessionDocument = (sessionDoc, loadOrder = 0) => ({
  ...sessionDoc.data(),
  fecha: getSessionDateInputValue(sessionDoc.data()),
  id: sessionDoc.id,
  _loadOrder: loadOrder,
});

const SesionesAlumno = ({ alumnoId }) => {
  const { id: routeAlumnoId } = useParams();
  const id = alumnoId || routeAlumnoId;
  const db = getFirestore();
  const [fichas, setFichas] = useState([]);
  const [moduleFilter, setModuleFilter] = useState('');
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [dirtyRows, setDirtyRows] = useState({});
  const savedSnapshotsRef = useRef({});
  const rowInputRefs = useRef({});
  const switchingRowRef = useRef(false);
  const addingRowRef = useRef(false);
  const pendingOperationsRef = useRef(new Set());
  const [pendingRows, setPendingRows] = useState({});

  useEffect(() => {
    const fetchFichas = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'sesiones'));
        const sesiones = [];
        querySnapshot.docs.forEach((sessionDoc, index) => {
          const session = normalizeSessionDocument(sessionDoc, index);
          if (session.alumnoId === id) sesiones.push(session);
        });
        const sortedSessions = sesiones.sort(compareSessionsForDisplay);
        savedSnapshotsRef.current = sortedSessions.reduce((snapshots, session) => {
          snapshots[session.id] = serializeSession(session);
          return snapshots;
        }, {});
        setDirtyRows({});
        setPendingRows({});
        setActiveSessionId(null);
        setFichas(sortedSessions);
      } catch (error) {
        console.error('Error fetching fichas:', error);
      }
    };

    fetchFichas();
  }, [id, db]);

  const moduleOptions = useMemo(() => Array.from(new Set(
    fichas.map((session) => String(session.modulo || '').trim()).filter(Boolean),
  )).sort((first, second) => compareModulesDescending({ modulo: first }, { modulo: second })), [fichas]);

  const visibleSessions = useMemo(() => {
    const filteredSessions = fichas.filter(
      (session) => !moduleFilter || String(session.modulo || '') === moduleFilter,
    );

    return moduleFilter
      ? [...filteredSessions].sort(compareSessionsForDisplay)
      : filteredSessions;
  }, [fichas, moduleFilter]);

  const beginPendingOperation = (sessionId) => {
    if (pendingOperationsRef.current.has(sessionId)) return false;
    pendingOperationsRef.current.add(sessionId);
    setPendingRows((current) => ({ ...current, [sessionId]: true }));
    return true;
  };

  const endPendingOperation = (sessionId) => {
    pendingOperationsRef.current.delete(sessionId);
    setPendingRows((current) => {
      const next = { ...current };
      delete next[sessionId];
      return next;
    });
  };

  const focusRow = (sessionId) => {
    if (!sessionId) return;
    window.setTimeout(() => rowInputRefs.current[sessionId]?.focus(), 0);
  };

  const removeSessionLocally = (sessionId) => {
    setFichas((current) => current.filter((session) => session.id !== sessionId));
    setDirtyRows((current) => {
      const next = { ...current };
      delete next[sessionId];
      return next;
    });
    delete savedSnapshotsRef.current[sessionId];
    delete rowInputRefs.current[sessionId];
    if (activeSessionId === sessionId) setActiveSessionId(null);
  };

  const handleChange = (sessionId, field, value) => {
    setFichas((current) => current.map((session) => {
      if (session.id !== sessionId) return session;

      const updatedSession = { ...session, [field]: value };
      setDirtyRows((previous) => ({
        ...previous,
        [sessionId]: serializeSession(updatedSession) !== savedSnapshotsRef.current[sessionId],
      }));
      return updatedSession;
    }));
  };

  const saveSessionById = async (sessionId, options = {}) => {
    const { showSuccess = true } = options;
    const session = fichas.find((item) => item.id === sessionId);

    if (!session) return false;
    if (!beginPendingOperation(sessionId)) return false;
    if (isSessionBlank(session)) {
      endPendingOperation(sessionId);
      Swal.fire({
        icon: 'warning',
        title: 'Fila vacía',
        text: 'No se puede guardar una fila en blanco. Agrega al menos un dato o elimínala.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
      focusRow(sessionId);
      return false;
    }

    try {
      const payload = buildSessionPayload(session);

      if (isDraftSession(session)) {
        const registeredAt = new Date();
        const persistedId = session.persistenceId
          || String(session.id).replace(DRAFT_PREFIX, '');
        await setDoc(doc(db, 'sesiones', persistedId), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        const persistedSession = {
          ...payload,
          id: persistedId,
          isDraft: false,
          createdAt: registeredAt,
          _loadOrder: session._loadOrder,
        };

        setFichas((current) => current
          .map((item) => (item.id === sessionId ? persistedSession : item))
          .sort(compareSessionsForDisplay));
        delete savedSnapshotsRef.current[sessionId];
        savedSnapshotsRef.current[persistedId] = serializeSession(persistedSession);
        setDirtyRows((previous) => {
          const next = { ...previous };
          delete next[sessionId];
          next[persistedId] = false;
          return next;
        });
        delete rowInputRefs.current[sessionId];
        if (activeSessionId === sessionId) setActiveSessionId(persistedId);
      } else {
        const registeredAt = session.createdAt || new Date();
        await updateDoc(
          doc(db, 'sesiones', session.id),
          session.createdAt ? payload : { ...payload, createdAt: serverTimestamp() },
        );
        const persistedSession = { ...session, ...payload, createdAt: registeredAt };
        setFichas((current) => current
          .map((item) => (item.id === session.id ? persistedSession : item))
          .sort(compareSessionsForDisplay));
        savedSnapshotsRef.current[session.id] = serializeSession(persistedSession);
        setDirtyRows((previous) => ({ ...previous, [session.id]: false }));
      }

      if (showSuccess) {
        Swal.fire({
          title: 'Guardado',
          text: 'La sesión ha sido guardada con éxito.',
          icon: 'success',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Aceptar',
        });
      }

      return true;
    } catch (error) {
      console.error('Error al guardar:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar',
        text: 'Ocurrió un error al intentar guardar la sesión.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
      return false;
    } finally {
      endPendingOperation(sessionId);
    }
  };

  const restoreSession = (sessionId) => {
    const snapshot = savedSnapshotsRef.current[sessionId];
    if (!snapshot) return;

    const restoredSession = JSON.parse(snapshot);
    setFichas((current) => current.map((session) => (
      session.id === sessionId ? { ...session, ...restoredSession } : session
    )));
    setDirtyRows((previous) => ({ ...previous, [sessionId]: false }));
  };

  const confirmLeaveCurrentRow = async () => {
    const currentSession = fichas.find((session) => session.id === activeSessionId);
    if (!currentSession) return true;

    const isDraft = isDraftSession(currentSession);
    const isBlank = isSessionBlank(currentSession);
    const isDirty = Boolean(dirtyRows[activeSessionId]);

    if (!isDraft && !isDirty) return true;

    if (isDraft && isBlank) {
      const result = await Swal.fire({
        title: 'La fila nueva está vacía',
        text: 'Para continuar, esta fila se debe descartar.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Descartar y continuar',
        cancelButtonText: 'Seguir editando',
        confirmButtonColor: '#0d6efd',
        cancelButtonColor: '#198754',
      });

      if (result.isConfirmed) {
        removeSessionLocally(activeSessionId);
        return true;
      }

      focusRow(activeSessionId);
      return false;
    }

    const result = await Swal.fire({
      title: 'Tienes cambios sin guardar',
      text: isDraft
        ? '¿Quieres guardar esta nueva fila antes de continuar?'
        : '¿Quieres guardar esta fila antes de continuar?',
      icon: 'warning',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Guardar y continuar',
      denyButtonText: 'Continuar sin guardar',
      cancelButtonText: 'Seguir editando',
      confirmButtonColor: '#0d6efd',
      denyButtonColor: '#6c757d',
      cancelButtonColor: '#198754',
    });

    if (result.isConfirmed) return saveSessionById(activeSessionId, { showSuccess: false });

    if (result.isDenied) {
      if (isDraft) removeSessionLocally(activeSessionId);
      else restoreSession(activeSessionId);
      return true;
    }

    focusRow(activeSessionId);
    return false;
  };

  const activateRow = async (sessionId) => {
    if (!sessionId || sessionId === activeSessionId || switchingRowRef.current) return;

    switchingRowRef.current = true;
    const canContinue = await confirmLeaveCurrentRow();
    switchingRowRef.current = false;

    if (canContinue) setActiveSessionId(sessionId);
    else focusRow(activeSessionId);
  };

  const handleModuleFilterChange = async (nextModule) => {
    if (nextModule === moduleFilter) return;
    const canContinue = await confirmLeaveCurrentRow();
    if (!canContinue) return;

    setActiveSessionId(null);
    setFichas((current) => [...current].sort(compareSessionsForDisplay));
    setModuleFilter(nextModule);
  };

  const handleAddRow = async () => {
    if (addingRowRef.current) return;
    addingRowRef.current = true;

    try {
      const existingDraft = fichas.find((session) => isDraftSession(session));
      if (existingDraft && existingDraft.id !== activeSessionId) {
        setModuleFilter('');
        setActiveSessionId(existingDraft.id);
        focusRow(existingDraft.id);
        Swal.fire({
          icon: 'info',
          title: 'Fila pendiente',
          text: 'Primero guarda o descarta la fila nueva antes de crear otra.',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Aceptar',
        });
        return;
      }

      const emptySavedSession = fichas.find(
        (session) => !isDraftSession(session) && isSessionBlank(session),
      );
      if (emptySavedSession) {
        setModuleFilter('');
        setActiveSessionId(emptySavedSession.id);
        focusRow(emptySavedSession.id);
        Swal.fire({
          icon: 'info',
          title: 'Fila sin registro',
          text: 'Completa o elimina la fila vacía antes de crear una nueva.',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Aceptar',
        });
        return;
      }

      const canContinue = await confirmLeaveCurrentRow();
      if (!canContinue) return;

      const persistenceId = createSessionDocumentId();
      const draftId = `${DRAFT_PREFIX}${persistenceId}`;
      const draftSession = {
        ...buildBlankSession(id),
        id: draftId,
        persistenceId,
        isDraft: true,
        _loadOrder: fichas.length,
      };

      savedSnapshotsRef.current[draftId] = serializeSession(draftSession);
      setDirtyRows((previous) => ({ ...previous, [draftId]: false }));
      setModuleFilter('');
      setFichas((current) => [...current, draftSession]);
      setActiveSessionId(draftId);
      focusRow(draftId);
    } finally {
      addingRowRef.current = false;
    }
  };

  const handleDelete = async (sessionId) => {
    const session = fichas.find((item) => item.id === sessionId);
    if (!session) return;

    const isDraft = isDraftSession(session);
    const result = await Swal.fire({
      title: isDraft ? '¿Descartar esta sesión?' : '¿Eliminar esta sesión?',
      text: isDraft
        ? 'La fila nueva y sus datos se perderán.'
        : 'Esta acción eliminará definitivamente el registro.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: isDraft ? 'Sí, descartar' : 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
    });

    if (!result.isConfirmed) return;
    if (isDraft) {
      removeSessionLocally(sessionId);
      return;
    }
    if (!beginPendingOperation(sessionId)) return;

    try {
      await deleteDoc(doc(db, 'sesiones', session.id));
      removeSessionLocally(sessionId);
      Swal.fire({
        title: 'Eliminado',
        text: 'La sesión ha sido eliminada con éxito.',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
    } catch (error) {
      console.error('Error al eliminar:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al eliminar',
        text: 'Ocurrió un error al intentar eliminar la sesión.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Aceptar',
      });
    } finally {
      endPendingOperation(sessionId);
    }
  };

  return (
    <section className="sesiones-alumno">
      <header className="sesiones-alumno__hero">
        <div className="sesiones-alumno__hero-copy">
          <span className="sesiones-alumno__eyebrow">Seguimiento académico</span>
          <h2>Ficha del Alumno</h2>
          <p>Registra el avance de cada sesión y conserva las observaciones de la práctica.</p>
        </div>

        <div className="sesiones-alumno__summary" aria-label={`${fichas.length} sesiones registradas`}>
          <FaClipboardCheck aria-hidden="true" />
          <div>
            <strong>{fichas.length}</strong>
            <span>{fichas.length === 1 ? 'sesión registrada' : 'sesiones registradas'}</span>
          </div>
        </div>

        <div className="sesiones-alumno__toolbar">
          <Form.Group className="sesiones-alumno__filter" controlId="sesiones-module-filter">
            <Form.Label>Filtrar por módulo</Form.Label>
            <Form.Select
              value={moduleFilter}
              onChange={(event) => { void handleModuleFilterChange(event.target.value); }}
            >
              <option value="">Todos los módulos</option>
              {moduleOptions.map((module) => (
                <option key={module} value={module}>{`Módulo ${module}`}</option>
              ))}
            </Form.Select>
            <Form.Text>Módulos de mayor a menor y sesiones recientes primero.</Form.Text>
          </Form.Group>
          <Button variant="success" className="sesiones-alumno__add-button" onClick={handleAddRow}>
            <FaPlus aria-hidden="true" />
            Añadir sesión
          </Button>
        </div>
      </header>

      <DocumentosHistoricosAlumno alumnoId={id} />

      <div className="sesiones-alumno__table-card">
        {visibleSessions.length === 0 ? (
          <div className="sesiones-alumno__empty-state">
            <FaClipboardCheck aria-hidden="true" />
            <h3>{fichas.length === 0 ? 'No hay sesiones registradas' : 'No hay sesiones en este módulo'}</h3>
            <p>
              {fichas.length === 0
                ? 'Usa “Añadir sesión” para comenzar la ficha de seguimiento del alumno.'
                : 'Selecciona otro módulo o muestra todos los registros.'}
            </p>
          </div>
        ) : (
          <div className="sesiones-alumno__table-scroll">
            <Table hover className="sesiones-alumno__table align-middle mb-0">
              <thead>
                <tr>
                  <th>N.º sesión</th>
                  <th>Fecha</th>
                  <th>Módulo</th>
                  <th>Ejercicios</th>
                  <th>Velocidad</th>
                  <th>Comprensión</th>
                  <th>Lectura realizada</th>
                  <th>Observaciones y registro de práctica</th>
                  <th className="sesiones-alumno__actions-column">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibleSessions.map((ficha, index) => (
                  <tr key={ficha.id} className={ficha.id === activeSessionId ? 'is-active' : ''}>
                    <td className="sesiones-alumno__session-number">
                      <span>{index + 1}</span>
                    </td>
                    <td className="sesiones-alumno__session-date">
                      <Form.Control
                        type="date"
                        aria-label={`Fecha de la sesión ${index + 1}`}
                        value={ficha.fecha || ''}
                        onFocus={() => { void activateRow(ficha.id); }}
                        onChange={(event) => handleChange(ficha.id, 'fecha', event.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        ref={(element) => { rowInputRefs.current[ficha.id] = element; }}
                        aria-label={`Módulo de la sesión ${index + 1}`}
                        value={ficha.modulo}
                        onFocus={() => { void activateRow(ficha.id); }}
                        onChange={(e) => handleChange(ficha.id, 'modulo', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        aria-label={`Ejercicios de la sesión ${index + 1}`}
                        value={ficha.ejercicios}
                        onFocus={() => { void activateRow(ficha.id); }}
                        onChange={(e) => handleChange(ficha.id, 'ejercicios', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        aria-label={`Velocidad de la sesión ${index + 1}`}
                        value={ficha.velocidad}
                        onFocus={() => { void activateRow(ficha.id); }}
                        onChange={(e) => handleChange(ficha.id, 'velocidad', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        aria-label={`Comprensión de la sesión ${index + 1}`}
                        value={ficha.comprension}
                        onFocus={() => { void activateRow(ficha.id); }}
                        onChange={(e) => handleChange(ficha.id, 'comprension', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        aria-label={`Lectura realizada en la sesión ${index + 1}`}
                        value={ficha.lecturaRealizada}
                        onFocus={() => { void activateRow(ficha.id); }}
                        onChange={(e) => handleChange(ficha.id, 'lecturaRealizada', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        aria-label={`Observaciones de la sesión ${index + 1}`}
                        value={ficha.observaciones}
                        onFocus={() => { void activateRow(ficha.id); }}
                        onChange={(e) => handleChange(ficha.id, 'observaciones', e.target.value)}
                      />
                    </td>
                    <td className="sesiones-alumno__actions-column">
                      <div className="sesiones-alumno__actions">
                        <Button
                          variant="success"
                          disabled={Boolean(pendingRows[ficha.id])}
                          onClick={() => { void saveSessionById(ficha.id); }}
                        >
                          <FaSave aria-hidden="true" />
                          {pendingRows[ficha.id] ? 'Guardando…' : 'Guardar'}
                        </Button>
                        <Button
                          variant="outline-danger"
                          disabled={Boolean(pendingRows[ficha.id])}
                          onClick={() => { void handleDelete(ficha.id); }}
                        >
                          <FaTrashAlt aria-hidden="true" />
                          Eliminar
                        </Button>
                        {dirtyRows[ficha.id] && (
                          <span className="sesiones-alumno__dirty-badge">Cambios sin guardar</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </section>
  );
};

export default SesionesAlumno;
